/**
 * Shared styles and the ReferenceStatusPill component used by both the
 * Photo AI (INVI HQ, global) page and the Photo Configuration (per-dealer)
 * page. These two pages were split out of the original single Photo
 * Configuration monolith; this module is the single home for everything
 * they have in common so neither page duplicates style definitions.
 */
import React from "react"

import { THEME } from "../../config/theme"

export const ACCEPTED_IMAGE_TYPES = "image/jpeg,image/png,image/webp"


// Body-style coverage: `count` is how many of the `total` body styles have
// at least one reference — not the number of photos, since a body style
// can hold several.
export function ReferenceStatusPill({ count, total }: { count: number; total: number }) {
  if (count === 0) {
    return <span style={pillEmptyStyle}>No references</span>
  }
  if (count >= total) {
    return <span style={pillCompleteStyle}>Complete</span>
  }
  return (
    <span style={pillPartialStyle}>
      {count} of {total} body styles
    </span>
  )
}


// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

export const containerStyle: React.CSSProperties = {
  padding: THEME.spacing.lg,
  fontFamily: THEME.fonts.primary,
}

export const titleStyle: React.CSSProperties = {
  fontSize: 24,
  fontWeight: 500,
  letterSpacing: "-0.5px",
  color: THEME.colors.neutral.light.textPrimary,
  margin: 0,
}

export const subtitleStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
  marginTop: THEME.spacing.xs,
  marginBottom: THEME.spacing.lg,
}

export const selectorRowStyle: React.CSSProperties = {
  display: "flex",
  gap: THEME.spacing.md,
  flexWrap: "wrap",
  marginBottom: THEME.spacing.md,
}

export const fieldColumnStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: THEME.spacing.sm,
  flex: "0 1 280px",
  minWidth: 220,
}

export const fieldLabelStyle: React.CSSProperties = {
  fontSize: THEME.typography.caption.size,
  fontWeight: THEME.typography.caption.weight,
  letterSpacing: THEME.typography.caption.tracking,
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
}

export const fieldHintStyle: React.CSSProperties = {
  fontSize: 12,
  color: THEME.colors.neutral.light.textSecondary,
  margin: 0,
}

export const selectStyle: React.CSSProperties = {
  fontSize: 13,
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  background: THEME.colors.neutral.light.surface,
  color: THEME.colors.neutral.light.textPrimary,
  fontFamily: THEME.fonts.primary,
  cursor: "pointer",
}

export const tabsRowStyle: React.CSSProperties = {
  display: "flex",
  gap: THEME.spacing.lg,
  borderBottom: `1px solid ${THEME.colors.silver}`,
  marginBottom: THEME.spacing.md,
}

export const tabButtonStyle = (active: boolean): React.CSSProperties => ({
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  fontSize: 13,
  fontWeight: active ? 500 : 400,
  color: active
    ? THEME.colors.neutral.light.textPrimary
    : THEME.colors.neutral.light.textSecondary,
  background: "transparent",
  border: "none",
  borderBottom: active ? `3px solid ${THEME.colors.navy}` : "3px solid transparent",
  cursor: "pointer",
  fontFamily: THEME.fonts.primary,
})

export const errorBannerStyle: React.CSSProperties = {
  background: THEME.colors.danger,
  border: `1px solid ${THEME.colors.semantic.danger}`,
  borderRadius: THEME.borderRadius.md,
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  color: THEME.colors.semantic.danger,
  fontSize: 13,
  marginBottom: THEME.spacing.md,
}

export const loadingTextStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
}

export const emptyStateStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: THEME.spacing.md,
  padding: THEME.spacing.lg,
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
}

export const emptyStateTextStyle: React.CSSProperties = {
  fontSize: 14,
  color: THEME.colors.neutral.light.textSecondary,
  margin: 0,
}

export const primaryButtonStyle: React.CSSProperties = {
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  fontSize: 14,
  fontWeight: 500,
  background: THEME.colors.navy,
  color: THEME.colors.white,
  border: "none",
  borderRadius: THEME.borderRadius.md,
  cursor: "pointer",
  fontFamily: THEME.fonts.primary,
}

export const secondaryButtonStyle: React.CSSProperties = {
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  fontSize: 14,
  fontWeight: 500,
  background: THEME.colors.neutral.light.surface,
  color: THEME.colors.neutral.light.textPrimary,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  cursor: "pointer",
  fontFamily: THEME.fonts.primary,
}

export const smallButtonStyle: React.CSSProperties = {
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  fontSize: 12,
  fontWeight: 500,
  background: THEME.colors.neutral.light.surface,
  color: THEME.colors.neutral.light.textPrimary,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.sm,
  cursor: "pointer",
  fontFamily: THEME.fonts.primary,
}

export const dangerButtonStyle: React.CSSProperties = {
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  fontSize: 14,
  fontWeight: 500,
  background: THEME.colors.neutral.light.surface,
  color: THEME.colors.semantic.danger,
  border: `1px solid ${THEME.colors.semantic.danger}`,
  borderRadius: THEME.borderRadius.md,
  cursor: "pointer",
  fontFamily: THEME.fonts.primary,
}

export const libraryToolbarStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: THEME.spacing.md,
}

export const libraryToolbarLabelStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
}

export const listContainerStyle: React.CSSProperties = {
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  overflow: "hidden",
}

export const listColumnLabelStyle: React.CSSProperties = {
  fontSize: THEME.typography.caption.size,
  fontWeight: THEME.typography.caption.weight,
  letterSpacing: THEME.typography.caption.tracking,
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
}

// Reference Library list — beefy rows with thumbnail / capture name /
// status pill / chevron. Inspired by INVI's audit table aesthetic.
export const LIBRARY_ROW_GRID = "120px 1fr 200px 32px"

export const libraryListHeaderStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: LIBRARY_ROW_GRID,
  gap: THEME.spacing.md,
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  borderBottom: `1px solid ${THEME.colors.silver}`,
  background: THEME.colors.lightGray,
  alignItems: "center",
}

export const libraryRowStyle = (hovered: boolean): React.CSSProperties => ({
  display: "grid",
  gridTemplateColumns: LIBRARY_ROW_GRID,
  gap: THEME.spacing.md,
  alignItems: "center",
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  borderBottom: `1px solid ${THEME.colors.silver}`,
  background: hovered ? THEME.colors.lightGray : THEME.colors.neutral.light.surface,
  cursor: "pointer",
  transition: "background 120ms ease",
})

export const libraryRowThumbnailCellStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
}

export const libraryRowThumbnailImageStyle: React.CSSProperties = {
  width: 96,
  height: 72,
  objectFit: "cover",
  borderRadius: THEME.borderRadius.md,
  background: THEME.colors.lightGray,
  border: `1px solid ${THEME.colors.silver}`,
}

export const libraryRowThumbnailPlaceholderStyle: React.CSSProperties = {
  width: 96,
  height: 72,
  background: THEME.colors.lightGray,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
}

export const libraryRowNameCellStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: THEME.spacing.sm,
  minWidth: 0,
}

export const libraryRowNameStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 500,
  color: THEME.colors.neutral.light.textPrimary,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
}

export const vehicleTypePillRowStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
  alignItems: "center",
}

// Per-vehicle-type pill — same pill shape as the status pills but
// smaller text and colored from THEME.vehicleTypeColors. The
// `background` and `color` properties get overridden per usage.
export const vehicleTypePillBaseStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  padding: "2px 8px",
  borderRadius: THEME.borderRadius.pill,
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: "0.2px",
}

export const libraryRowStatusCellStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
}

export const libraryRowChevronCellStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}

// Status pills used by both library rows and bucket-shot rows.
export const statusPillBaseStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  borderRadius: THEME.borderRadius.pill,
  fontSize: 12,
  fontWeight: 500,
}

export const pillEmptyStyle: React.CSSProperties = {
  ...statusPillBaseStyle,
  background: THEME.colors.lightGray,
  color: THEME.colors.mediumGray,
}

export const pillPartialStyle: React.CSSProperties = {
  ...statusPillBaseStyle,
  background: THEME.colors.info,
  color: THEME.colors.neutral.light.textPrimary,
}

export const pillCompleteStyle: React.CSSProperties = {
  ...statusPillBaseStyle,
  background: THEME.colors.success,
  color: THEME.colors.text,
}

// Capture Order / Publish Order tab — four-column grid (order, name,
// reference status, action). The Capture Order tab overrides
// gridTemplateColumns at the call site to fit a fifth Start Zoom
// column (a capture-time concern; Publish Order stays four).
export const orderListHeaderStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "120px 1fr 200px 100px",
  gap: THEME.spacing.md,
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  borderBottom: `1px solid ${THEME.colors.silver}`,
  background: THEME.colors.lightGray,
}

export const orderListRowStyle = (dragging: boolean): React.CSSProperties => ({
  display: "grid",
  gridTemplateColumns: "120px 1fr 200px 100px",
  gap: THEME.spacing.md,
  alignItems: "center",
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  borderBottom: `1px solid ${THEME.colors.silver}`,
  background: dragging ? THEME.colors.lightGray : THEME.colors.neutral.light.surface,
  cursor: "grab",
  opacity: dragging ? 0.6 : 1,
})

export const listOrderCellStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: THEME.spacing.sm,
  fontSize: 13,
  color: THEME.colors.neutral.light.textPrimary,
}

export const dragHandleStyle: React.CSSProperties = {
  cursor: "grab",
  color: THEME.colors.mediumGray,
  fontSize: 16,
}

export const listShotCellStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
}

export const listZoomCellStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
}

// Compact per-row select — the page-level selectStyle is sized for the
// toolbar's vehicle-type picker, too heavy to repeat 36 times in a list.
export const zoomSelectStyle: React.CSSProperties = {
  padding: `4px ${THEME.spacing.sm}`,
  fontSize: 13,
  color: THEME.colors.neutral.light.textPrimary,
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.sm,
}

export const shotNameStyle: React.CSSProperties = {
  fontSize: 14,
  color: THEME.colors.neutral.light.textPrimary,
}

export const shotSlugStyle: React.CSSProperties = {
  fontSize: 11,
  color: THEME.colors.neutral.light.textSecondary,
  fontFamily: THEME.fonts.mono,
}

export const listReferenceCellStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
}

export const listActionsCellStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
}

// Test Classification panel
export const classifyContainerStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "minmax(280px, 1fr) minmax(320px, 1fr)",
  gap: THEME.spacing.lg,
}

export const classifyLeftColumnStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: THEME.spacing.md,
}

export const classifyRightColumnStyle: React.CSSProperties = {
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  padding: THEME.spacing.md,
  minHeight: 320,
}

export const classifyDescriptionCardStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: THEME.spacing.xs,
  padding: THEME.spacing.md,
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
}

export const classifyDescriptionLabelStyle: React.CSSProperties = {
  fontSize: THEME.typography.caption.size,
  fontWeight: THEME.typography.caption.weight,
  letterSpacing: THEME.typography.caption.tracking,
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
}

export const classifyDescriptionParagraphStyle: React.CSSProperties = {
  fontSize: 13,
  lineHeight: 1.5,
  color: THEME.colors.neutral.light.textPrimary,
  margin: 0,
}

export const classifyContextRowStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  background: THEME.colors.info,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  marginBottom: THEME.spacing.sm,
}

export const classifyContextLabelStyle: React.CSSProperties = {
  fontSize: THEME.typography.caption.size,
  fontWeight: THEME.typography.caption.weight,
  letterSpacing: THEME.typography.caption.tracking,
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
}

export const classifyContextValueStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 500,
  color: THEME.colors.neutral.light.textPrimary,
}

export const hiddenInputStyle: React.CSSProperties = {
  display: "none",
}

export const resultHeaderRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: THEME.spacing.md,
}

export const resultHeaderLabelStyle: React.CSSProperties = {
  fontSize: THEME.typography.caption.size,
  fontWeight: THEME.typography.caption.weight,
  letterSpacing: THEME.typography.caption.tracking,
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
}

export const matchesListStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: THEME.spacing.sm,
}

export const matchRowStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "24px 1fr 64px 80px",
  gap: THEME.spacing.sm,
  alignItems: "center",
  padding: THEME.spacing.sm,
  background: THEME.colors.lightGray,
  borderRadius: THEME.borderRadius.sm,
}

export const matchRankStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: THEME.colors.neutral.light.textSecondary,
}

export const matchNameColumnStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
}

export const matchDisplayNameStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textPrimary,
}

export const matchSlugStyle: React.CSSProperties = {
  fontSize: 11,
  color: THEME.colors.neutral.light.textSecondary,
  fontFamily: THEME.fonts.mono,
}

export const matchSimilarityStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textPrimary,
  textAlign: "right",
  fontFamily: THEME.fonts.mono,
}

export const similarityBarTrackStyle: React.CSSProperties = {
  height: 6,
  background: THEME.colors.silver,
  borderRadius: 3,
  overflow: "hidden",
}

export const similarityBarFillStyle = (similarity: number): React.CSSProperties => ({
  width: `${Math.max(0, Math.min(1, similarity)) * 100}%`,
  height: "100%",
  background: similarity >= 0.75 ? THEME.colors.semantic.success : THEME.colors.semantic.warning,
})

export const modalOverlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.4)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
}

export const modalStyle: React.CSSProperties = {
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  width: "min(480px, 92vw)",
  maxHeight: "90vh",
  overflowY: "auto",
  padding: THEME.spacing.lg,
  fontFamily: THEME.fonts.primary,
}

// The image-type editor: wide enough for a body style's row of reference
// thumbnails (the approved mockup, 2026-10-01, is 1120px).
export const largeModalStyle: React.CSSProperties = {
  ...modalStyle,
  width: "min(1120px, 95vw)",
}

export const modalTitleStyle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 500,
  color: THEME.colors.neutral.light.textPrimary,
  margin: 0,
  marginBottom: THEME.spacing.md,
}

export const modalFieldStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: THEME.spacing.sm,
  marginBottom: THEME.spacing.md,
}

export const inputStyle: React.CSSProperties = {
  fontSize: 13,
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  background: THEME.colors.neutral.light.surface,
  color: THEME.colors.neutral.light.textPrimary,
  fontFamily: THEME.fonts.primary,
}

export const slugDisplayStyle: React.CSSProperties = {
  fontSize: 13,
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  background: THEME.colors.lightGray,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  color: THEME.colors.neutral.light.textSecondary,
  fontFamily: THEME.fonts.mono,
}

export const modalActionsRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginTop: THEME.spacing.md,
  marginBottom: THEME.spacing.md,
}

export const modalActionsRightStyle: React.CSSProperties = {
  display: "flex",
  gap: THEME.spacing.sm,
}

export const referenceGridSectionStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: THEME.spacing.sm,
  paddingTop: THEME.spacing.md,
  borderTop: `1px solid ${THEME.colors.silver}`,
}

// Image-type editor references: one row per body style — its name and
// photo count on the left, its thumbnails plus an add tile wrapping on the
// right. A body style holds several references, so the row grows.
const REFERENCE_TILE_WIDTH = 132

// Wraps on a phone: the thumbnails drop below the name instead of
// squeezing into one column beside it.
export const referenceRowStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: THEME.spacing.md,
  padding: `${THEME.spacing.sm} 0`,
  borderBottom: `1px solid ${THEME.colors.neutral.light.border}`,
}

export const referenceRowHeadStyle: React.CSSProperties = {
  width: 120,
  flex: "none",
  display: "flex",
  flexDirection: "column",
  gap: 4,
}

export const referenceRowNameStyle: React.CSSProperties = {
  fontSize: THEME.typography.caption.size,
  fontWeight: 600,
  letterSpacing: THEME.typography.caption.tracking,
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textPrimary,
}

export const referenceRowCountStyle: React.CSSProperties = {
  fontSize: THEME.typography.caption.size,
  color: THEME.colors.neutral.light.textSecondary,
}

export const referenceTileRowStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: THEME.spacing.sm,
  flex: "1 1 260px",
  minWidth: 0,
}

export const referenceThumbnailStyle: React.CSSProperties = {
  position: "relative",
  width: REFERENCE_TILE_WIDTH,
  aspectRatio: "4 / 3",
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.sm,
  overflow: "hidden",
}

export const referenceThumbnailImageStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
}

export const referenceRemoveButtonStyle: React.CSSProperties = {
  position: "absolute",
  top: 5,
  right: 5,
  width: 22,
  height: 22,
  padding: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: THEME.borderRadius.pill,
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  color: THEME.colors.semantic.danger,
  cursor: "pointer",
}

// The "Remove this reference?" check that covers a thumbnail after its ×
// is clicked — one photo at a time, so a stray click never loses one.
export const referenceRemoveConfirmStyle: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  padding: 6,
  background: THEME.modal.overlay,
  color: THEME.colors.neutral.light.surface,
  fontSize: THEME.typography.caption.size,
  fontWeight: 500,
  textAlign: "center",
}

export const referenceRemoveConfirmActionsStyle: React.CSSProperties = {
  display: "flex",
  gap: 6,
}

export const referenceConfirmButtonStyle = (danger: boolean): React.CSSProperties => ({
  padding: "3px 10px",
  fontSize: THEME.typography.caption.size,
  fontWeight: 500,
  border: "none",
  borderRadius: THEME.borderRadius.sm,
  background: danger ? THEME.colors.semantic.danger : THEME.colors.neutral.light.surface,
  color: danger ? THEME.colors.neutral.light.surface : THEME.colors.neutral.light.textPrimary,
  cursor: "pointer",
  fontFamily: THEME.fonts.primary,
})

export const referenceAddTileStyle = (dragHover: boolean, busy: boolean): React.CSSProperties => ({
  width: REFERENCE_TILE_WIDTH,
  aspectRatio: "4 / 3",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 4,
  cursor: busy ? "progress" : "pointer",
  background: dragHover ? THEME.colors.lightGray : THEME.colors.neutral.light.surface,
  border: `2px dashed ${dragHover ? THEME.colors.navy : THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.sm,
  color: THEME.colors.neutral.light.textSecondary,
  fontSize: 11,
  textAlign: "center",
  transition: "border-color 120ms ease, background 120ms ease",
})

export const modalDropZoneStyle = (dragHover: boolean): React.CSSProperties => ({
  display: "block",
  cursor: "pointer",
  background: dragHover ? THEME.colors.lightGray : THEME.colors.neutral.light.surface,
  border: `2px dashed ${dragHover ? THEME.colors.navy : THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  minHeight: 180,
  overflow: "hidden",
  position: "relative",
  transition: "border-color 120ms ease, background 120ms ease",
})

export const modalDropZonePlaceholderStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: THEME.spacing.sm,
  height: 180,
  padding: THEME.spacing.md,
  textAlign: "center",
}

export const modalDropZoneTitleStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  fontSize: 14,
  fontWeight: 500,
  color: THEME.colors.neutral.light.textPrimary,
}

export const modalDropZoneHintStyle: React.CSSProperties = {
  fontSize: 12,
  color: THEME.colors.neutral.light.textSecondary,
  maxWidth: 340,
}

export const modalDropZonePreviewWrapperStyle: React.CSSProperties = {
  position: "relative",
}

export const modalDropZonePreviewImageStyle: React.CSSProperties = {
  width: "100%",
  maxHeight: 220,
  objectFit: "contain",
  display: "block",
  background: THEME.colors.lightGray,
}

export const modalDropZoneOverlayHintStyle: React.CSSProperties = {
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  fontSize: 11,
  color: THEME.colors.white,
  background: "rgba(0,0,0,0.55)",
  textAlign: "center",
}

export const pickerListStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: THEME.spacing.xs,
  maxHeight: 360,
  overflowY: "auto",
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  padding: THEME.spacing.xs,
}

export const pickerRowStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 2,
  padding: THEME.spacing.sm,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  textAlign: "left",
  borderRadius: THEME.borderRadius.sm,
  fontFamily: THEME.fonts.primary,
}
